import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Cancel as CancelIcon,
  CheckCircle as ApproveIcon,
  Close as RejectIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import type { GroupCreationRequest } from '@penticton-dev-events/shared';

import GroupCreationRequestStatusBadge from './GroupCreationRequestStatusBadge';

export interface GroupCreationRequestCardProps {
  /**
   * The group creation request to display
   */
  readonly request: GroupCreationRequest;

  /**
   * Display variant:
   * - 'user-compact': Minimal view for user's request list
   * - 'user-detailed': Full details for user viewing their request
   * - 'admin': Full details with admin actions
   */
  readonly variant: 'user-compact' | 'user-detailed' | 'admin';

  /**
   * Callback when approve button is clicked (admin only)
   */
  readonly onApprove?: (requestId: string) => void;

  /**
   * Callback when reject button is clicked (admin only)
   */
  readonly onReject?: (requestId: string) => void;

  /**
   * Callback when cancel button is clicked (user only, pending requests)
   */
  readonly onCancel?: (requestId: string) => void;

  /**
   * Callback when view/details button is clicked
   */
  readonly onView?: (requestId: string) => void;

  /**
   * Callback when group link is clicked (approved requests only)
   */
  readonly onGroupLink?: (groupId: string) => void;

  /**
   * Loading state for action buttons
   */
  readonly loading?: boolean;
}

/**
 * Card component for displaying group creation requests
 *
 * Features:
 * - Three display variants (user-compact, user-detailed, admin)
 * - Conditional actions based on status and variant
 * - Responsive layout
 * - Accessibility (ARIA labels, keyboard navigation, semantic HTML)
 * - Loading states for async actions
 *
 * Typography Hierarchy:
 * - h6: Request name
 * - body1: Summary
 * - body2: Metadata (dates, reviewer)
 * - caption: Additional context
 */
const GroupCreationRequestCard = ({
  request,
  variant,
  onApprove,
  onReject,
  onCancel,
  onView,
  onGroupLink,
  loading = false,
}: GroupCreationRequestCardProps) => {
  const isCompact = variant === 'user-compact';
  const isAdmin = variant === 'admin';
  const isPending = request.status === 'pending';
  const isApproved = request.status === 'approved';
  const isRejected = request.status === 'rejected';

  const formatDate = (isoDate: string) => {
    return new Date(isoDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const CardHeaderComponent = (
    <CardHeader
      title={request.name}
      titleTypographyProps={{
        variant: 'h6',
        component: 'h3',
      }}
      subheader={
        isCompact ? null : (
          <Stack spacing={0.5} mt={1}>
            <Typography variant="body2" color="text.secondary">
              Requested by {request.requesterName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDate(request.requestedAt)}
            </Typography>
          </Stack>
        )
      }
      action={
        <Stack direction="row" spacing={1} alignItems="center">
          <GroupCreationRequestStatusBadge status={request.status} size="medium" />
          {onView && (
            <Tooltip title="View details">
              <IconButton
                onClick={() => onView(request.id)}
                aria-label={`View details for ${request.name} request`}
                disabled={loading}
                size="small"
              >
                <ViewIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      }
    />
  );

  const CardContentComponent = !isCompact && (
    <CardContent>
      <Stack spacing={2}>
        {/* Summary */}
        {request.summary && (
          <Box>
            <Typography variant="body1" color="text.primary" gutterBottom>
              {request.summary}
            </Typography>
          </Box>
        )}

        {/* Settings */}
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip
            label={request.autoAcceptMembers ? 'Auto-accept members' : 'Manual approval'}
            size="small"
            variant="outlined"
            color={request.autoAcceptMembers ? 'success' : 'default'}
          />
          <Chip label={`Slug: ${request.slug}`} size="small" variant="outlined" />
        </Stack>

        {/* Admin/Review Information */}
        {isAdmin && (
          <Box>
            <Typography variant="body2" color="text.secondary">
              <strong>Requester Email:</strong> {request.requesterEmail}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Requester UID:</strong> {request.requesterUid}
            </Typography>
          </Box>
        )}

        {/* Approval/Rejection Details */}
        {(isApproved || isRejected) && request.reviewedAt && (
          <Box>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary">
              <strong>Reviewed:</strong> {formatDate(request.reviewedAt)}
            </Typography>
            {(request.reviewerName || request.reviewedBy) && (
              <Typography variant="body2" color="text.secondary">
                <strong>Reviewed by:</strong>{' '}
                {request.reviewerName || request.reviewedBy || 'Unknown'}
              </Typography>
            )}
            {request.reviewNote && (
              <Typography variant="body2" color="text.secondary" mt={1}>
                <strong>Review note:</strong> {request.reviewNote}
              </Typography>
            )}
          </Box>
        )}

        {/* Approved Group Link */}
        {isApproved && request.groupId && onGroupLink && (
          <Box>
            <Button
              startIcon={<LinkIcon />}
              onClick={() => onGroupLink(request.groupId!)}
              variant="outlined"
              size="small"
              aria-label={`Go to approved group ${request.name}`}
            >
              View Created Group
            </Button>
          </Box>
        )}
      </Stack>
    </CardContent>
  );

  const CardActionsComponent = !isCompact && (
    <CardActions sx={{ px: 2, pb: 2 }}>
      <Stack direction="row" spacing={1} width="100%" flexWrap="wrap">
        {/* Admin Actions */}
        {isAdmin && isPending && (
          <>
            <Button
              startIcon={<ApproveIcon />}
              onClick={() => onApprove?.(request.id)}
              variant="contained"
              color="success"
              disabled={loading}
              aria-label={`Approve request for ${request.name}`}
            >
              Approve
            </Button>
            <Button
              startIcon={<RejectIcon />}
              onClick={() => onReject?.(request.id)}
              variant="outlined"
              color="error"
              disabled={loading}
              aria-label={`Reject request for ${request.name}`}
            >
              Reject
            </Button>
          </>
        )}

        {/* User Actions */}
        {!isAdmin && isPending && onCancel && (
          <Button
            startIcon={<CancelIcon />}
            onClick={() => onCancel(request.id)}
            variant="outlined"
            color="warning"
            disabled={loading}
            aria-label={`Cancel request for ${request.name}`}
          >
            Cancel Request
          </Button>
        )}
      </Stack>
    </CardActions>
  );

  return (
    <Card
      elevation={2}
      sx={theme => ({
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.paper',
        borderRadius: 2,
        transition: theme.transitions.create(['box-shadow', 'transform'], {
          duration: theme.transitions.duration.short,
        }),
        '&:hover': {
          boxShadow: theme.shadows[4],
        },
      })}
      component="article"
      aria-label={`Group creation request for ${request.name}`}
    >
      {CardHeaderComponent}
      {CardContentComponent}
      {CardActionsComponent}
    </Card>
  );
};

export default GroupCreationRequestCard;
